import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { fundAPI } from '../services/api';
import { Plus, Edit, Trash2, Folder } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useMobile } from '../hooks/useMobile';
import { useCurrencyToggle } from '../hooks/useCurrencyToggle';

const Funds = () => {
  const { user } = useAuth();
  const { canDelete = false, canCreate = false, canEdit = false } = usePermissions() || {};
  const isMobile = useMobile();
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  // Currency + display-unit toggle (persisted)
  const { formatFromBuckets, controlsUI } = useCurrencyToggle('funds_currency');

  useEffect(() => {
    fetchFunds();
  }, []);

  const fetchFunds = async () => {
    try {
      const response = await fundAPI.getAll();
      setFunds(response.data);
    } catch (error) {
      console.error('Failed to fetch funds:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this fund?')) return;

    try {
      await fundAPI.delete(id);
      setFunds(funds.filter((f) => f.id !== id));
    } catch (error) {
      alert('Failed to delete fund');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="funds-title">Funds</h1>
          <p className="text-gray-600 mt-2">Manage Alternative Investment Funds (AIFs)</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {controlsUI}
          {canCreate && !isMobile && (
            <Link
              to="/funds/create"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              data-testid="create-fund-button"
            >
              <Plus size={20} className="mr-2" />
              Create Fund
            </Link>
          )}
        </div>
      </div>

      {funds.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Folder size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No funds yet</h3>
          <p className="text-gray-500 mb-6">{isMobile ? 'No funds to display' : 'Create your first fund to get started'}</p>
          {canCreate && !isMobile && (
            <Link
              to="/funds/create"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus size={20} className="mr-2" />
              Create Fund
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {funds.map((fund) => (
            <div
              key={fund.id}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow"
              data-testid={`fund-card-${fund.id}`}
            >
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{fund.name}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Currency:</span>
                    <span className="font-medium">{fund.fund_currency}</span>
                  </div>
                  {fund.actual_corpus ? (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Corpus:</span>
                      <span className="font-medium" data-testid={`fund-corpus-${fund.id}`}>
                        {formatFromBuckets(fund.totals_by_currency, 'corpus')}
                      </span>
                    </div>
                  ) : null}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Commitment:</span>
                    <span className="font-medium" data-testid={`fund-commitment-${fund.id}`}>
                      {formatFromBuckets(fund.totals_by_currency, 'commitment')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Called:</span>
                    <span className="font-medium" data-testid={`fund-called-${fund.id}`}>
                      {formatFromBuckets(fund.totals_by_currency, 'called')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Contribution:</span>
                    <span className="font-medium" data-testid={`fund-contribution-${fund.id}`}>
                      {formatFromBuckets(fund.totals_by_currency, 'contribution')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total NAV:</span>
                    <span className="font-medium" data-testid={`fund-nav-${fund.id}`}>
                      {formatFromBuckets(fund.totals_by_currency, 'nav')}
                    </span>
                  </div>
                  {fund.sebi_aif_category && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Category:</span>
                      <span className="font-medium">{fund.sebi_aif_category}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl flex justify-between">
                <Link
                  to={`/funds/${fund.id}`}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                  data-testid={`view-fund-${fund.id}`}
                >
                  View Details
                </Link>
                {(canEdit || canDelete) && !isMobile && (
                  <div className="flex space-x-2">
                    {canEdit && (
                      <Link
                        to={`/funds/${fund.id}/edit`}
                        className="text-gray-600 hover:text-gray-700"
                        title="Edit fund"
                      >
                        <Edit size={18} />
                      </Link>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(fund.id)}
                        className="text-red-600 hover:text-red-700"
                        title="Delete fund (Admin only)"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Funds;
